/**
 * Ouverture de la caisse (design 4.2) : au premier NIP, si aucun quart n'est
 * ouvert sur le poste, l'employe compte le fond de caisse et le confirme. Le
 * fond est prerempli par la configuration du magasin si elle en donne un
 * (default_opening_float_cents), sinon 200,00 $, et reste modifiable.
 *
 * Le quart est ecrit dans le magasin IndexedDB `shifts` (store/shifts.js) ;
 * un double clic n'ouvre qu'un quart. Affiche par FluxCaisse (etape
 * OUVERTURE), avant l'ecran de vente. `onVerrouiller` : retour a l'ecran NIP
 * sans ouvrir la caisse. `poste` ({ sync, onSync, canSync }) : a gauche,
 * l'etat du poste comme sur l'ecran de vente (employe connecte, vente
 * permise, catalogue et sa mise a jour).
 */
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ChampMontant from './ChampMontant';
import PaveMontant from './PaveMontant';
import { ajouterChiffres, effacerChiffre } from './saisieMontant';
import CatalogStatus from '../app/CatalogStatus';
import { palette } from '../app/theme';
import { chargerContexteRecu } from '../print/contexteRecu';
import { FOND_MAX_CENTS, fondPropose, openShift } from '../store/shifts';

const CARD_SX = {
    bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: '14px',
    boxShadow: '0 1px 3px rgba(15,23,42,.06)', p: 3,
};

export default function OuvertureCaisse({ db, session, onOuverte, onVerrouiller, poste = null, now }) {
    const [fond, setFond] = useState(null);
    const [busy, setBusy] = useState(false);
    const [erreur, setErreur] = useState(null);
    const envoi = useRef(false);

    useEffect(() => {
        let cancelled = false;
        chargerContexteRecu(db)
            .then(({ config }) => { if (!cancelled) setFond((current) => (current === null ? fondPropose(config) : current)); })
            .catch(() => { if (!cancelled) setFond((current) => (current === null ? fondPropose(null) : current)); });
        return () => { cancelled = true; };
    }, [db]);

    const pret = fond !== null && !busy;

    const ouvrir = async () => {
        if (!pret || envoi.current) return;
        envoi.current = true;
        setBusy(true);
        setErreur(null);
        try {
            const { shift } = await openShift(db, {
                employee: { employee_id: session.employee_id, display_name: session.display_name },
                openingFloatCents: fond,
                ...(now ? { now } : {}),
            });
            onOuverte(shift);
        } catch (_error) {
            envoi.current = false;
            setBusy(false);
            setErreur('La caisse n\'a pas pu être ouverte sur ce poste. Réessayez ; si le problème continue, appelez le responsable.');
        }
    };

    const chiffres = (c) => setFond((v) => ajouterChiffres(v ?? 0, c, FOND_MAX_CENTS));
    const effacer = () => setFond((v) => effacerChiffre(v ?? 0));

    const panneau = (
        <Box
            data-testid="ouverture-caisse"
            component="section"
            aria-labelledby="ouverture-titre"
            sx={{ ...CARD_SX, width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PointOfSaleIcon aria-hidden="true" sx={{ fontSize: 36, color: palette.primary }} />
                <Typography id="ouverture-titre" component="h2" sx={{ fontSize: 28, fontWeight: 800, color: palette.text, flex: 1 }}>
                    Ouvrir la caisse
                </Typography>
                {onVerrouiller && (
                    <Button variant="outlined" startIcon={<LockOutlinedIcon />} disabled={busy} onClick={onVerrouiller} sx={{ minHeight: 64 }}>
                        Verrouiller
                    </Button>
                )}
            </Box>
            <Typography sx={{ fontSize: 18, color: palette.textSecondary }}>
                Aucune caisse n&apos;est ouverte sur ce poste. Comptez le fond de caisse, corrigez-le au besoin, puis ouvrez la caisse.
            </Typography>
            <ChampMontant
                id="fond-de-caisse"
                testId="fond-de-caisse"
                label="Fond de caisse"
                cents={fond ?? 0}
                disabled={!pret}
                onChiffres={chiffres}
                onEffacer={effacer}
                onValider={ouvrir}
            />
            <PaveMontant label="Pavé du fond de caisse" disabled={!pret} onChiffres={chiffres} onEffacer={effacer} />
            {erreur && (
                <Typography role="alert" sx={{ fontSize: 18, fontWeight: 600, color: palette.errorOnTint, bgcolor: palette.errorTint, p: 1.5, borderRadius: 2 }}>
                    {erreur}
                </Typography>
            )}
            <Button
                variant="contained"
                disabled={!pret}
                onClick={ouvrir}
                sx={{ minHeight: 88, fontSize: 26, fontWeight: 800, borderRadius: '12px' }}
            >
                OUVRIR LA CAISSE
            </Button>
        </Box>
    );

    return (
        <Box data-testid="ecran-ouverture" sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2, p: 2, overflowY: 'auto' }}>
            {session && (
                <Box sx={{ ...CARD_SX, flex: 1, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Typography component="p" data-testid="session-employe" sx={{ fontSize: 28, fontWeight: 800, color: palette.text }}>
                        Bonjour, {session.display_name}
                    </Typography>
                    <Box
                        data-testid="acces-vente"
                        data-acces="autorise"
                        data-raison=""
                        role="status"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: 2, bgcolor: palette.successTint, color: palette.successOnTint }}
                    >
                        <CheckCircleIcon aria-hidden="true" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>Prêt pour la vente</Typography>
                            <Typography sx={{ fontSize: 18 }}>Ouvrez la caisse pour commencer les ventes.</Typography>
                        </Box>
                    </Box>
                    {poste && poste.sync && (
                        <CatalogStatus
                            catalogMeta={poste.sync.catalogMeta}
                            catalogSync={poste.sync.catalogSync}
                            syncing={poste.sync.syncing}
                            progress={poste.sync.progress}
                            onSync={poste.onSync}
                            canSync={poste.canSync}
                        />
                    )}
                </Box>
            )}
            {panneau}
        </Box>
    );
}
